import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Sparkles,
  Database,
  Target,
  MessageSquare,
  Star,
  Settings,
  Zap,
  Bot,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAgentStore } from '@/store/agentStore';
import { AgentType } from '@/types';
import { FileUploadZone } from '@/components/agents/FileUploadZone';
import { knowledgeService } from '@/services/knowledge.service';
import { cn } from '@/utils/cn';
import { MarkdownEditor } from '@/components/common/MarkdownEditor';

// ---------------------------------------------------------------------------
// Constants (matching AgentDetailPage)
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  'w-full h-10 px-3 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-colors';
const TEXTAREA_CLASS =
  'w-full px-3 py-2.5 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:shadow-[3px_3px_0px_#1a1a2e] outline-none transition-colors min-h-[100px] resize-none';

// ---------------------------------------------------------------------------
// SectionCard (matching AgentDetailPage)
// ---------------------------------------------------------------------------

const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, iconBg, subtitle, action, children }) => (
  <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#1a1a2e]">
      <div className="flex items-center gap-3">
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', iconBg)}>
          {icon}
        </div>
        <div>
          <h3 className="text-[13px] font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="px-6 py-5">{children}</div>
  </div>
);


// ---------------------------------------------------------------------------
// Template definitions
// ---------------------------------------------------------------------------

interface TemplateDefinition {
  type: AgentType;
  label: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  iconBg: string;
  defaults: {
    name: string;
    description: string;
    tone: 'professional' | 'friendly' | 'casual';
    language: string;
    greeting_message: string;
    goodbye_message: string;
    custom_instructions: string;
  };
}

const TEMPLATES: TemplateDefinition[] = [
  {
    type: 'sales',
    label: 'Sales',
    description: 'Help close deals and qualify leads',
    icon: Target,
    iconBg: 'bg-rose-50',
    defaults: {
      name: 'Sales Agent',
      description: 'Helps qualify leads and close deals',
      tone: 'professional',
      language: 'English',
      greeting_message:
        "Hi there! I'm here to help you find the right solution for your needs. What are you looking for?",
      goodbye_message: 'Thank you for your time! Looking forward to helping you further.',
      custom_instructions:
        'You are a sales assistant. Help qualify leads by understanding their needs, budget, and timeline. Be consultative, not pushy. Ask about their current challenges and how they\'re solving them today.',
    },
  },
  {
    type: 'support',
    label: 'Support',
    description: 'Customer support & FAQ answers',
    icon: MessageSquare,
    iconBg: 'bg-blue-50',
    defaults: {
      name: 'Support Agent',
      description: 'Provides customer support and answers questions',
      tone: 'friendly',
      language: 'English',
      greeting_message: 'Hello! How can I help you today?',
      goodbye_message: 'Thank you for chatting with us! Have a great day.',
      custom_instructions:
        "You are a customer support agent. Help customers with their questions and issues. Be empathetic and solution-oriented. If you can't solve a problem, offer to escalate to a human agent.",
    },
  },
  {
    type: 'onboarding',
    label: 'Onboarding',
    description: 'Guide new users through setup',
    icon: Zap,
    iconBg: 'bg-amber-50',
    defaults: {
      name: 'Onboarding Agent',
      description: 'Guides new users through setup and getting started',
      tone: 'friendly',
      language: 'English',
      greeting_message:
        "Welcome! I'm excited to help you get started. Let me walk you through the setup process.",
      goodbye_message: "You're all set! Feel free to reach out if you need anything.",
      custom_instructions:
        'You are an onboarding assistant. Guide new users through setup step by step. Be encouraging and patient. Explain features simply and offer tips.',
    },
  },
  {
    type: 'lead_generation',
    label: 'Lead Gen',
    description: 'Capture & qualify potential customers',
    icon: Star,
    iconBg: 'bg-violet-50',
    defaults: {
      name: 'Lead Generation Agent',
      description: 'Captures and qualifies potential customers',
      tone: 'casual',
      language: 'English',
      greeting_message: 'Hey! Thanks for stopping by. Can I help you with anything?',
      goodbye_message: 'Thanks for chatting! We\'ll be in touch soon.',
      custom_instructions:
        "You are a lead generation agent. Engage visitors in natural conversation to understand their needs. Collect contact info naturally, don't make it feel like a form. Qualify based on fit and intent.",
    },
  },
  {
    type: 'custom',
    label: 'Custom',
    description: 'Start from scratch',
    icon: Settings,
    iconBg: 'bg-gray-50',
    defaults: {
      name: '',
      description: '',
      tone: 'professional',
      language: 'English',
      greeting_message: 'Hello! How can I help you today?',
      goodbye_message: 'Thank you for chatting with us!',
      custom_instructions: '',
    },
  },
];

// ---------------------------------------------------------------------------
// Form state interface
// ---------------------------------------------------------------------------

interface FormState {
  name: string;
  description: string;
  type: AgentType;
  status: 'draft' | 'active' | 'testing';
  tone: 'professional' | 'friendly' | 'casual';
  language: string;
  greeting_message: string;
  goodbye_message: string;
  custom_instructions: string;
  ai_provider: string;
  ai_model: string;
  ai_temperature: number;
  ai_max_tokens: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CreateAgentPage: React.FC = () => {
  const navigate = useNavigate();
  const { createAgent } = useAgentStore();

  const [step, setStep] = useState<'template' | 'configure'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<AgentType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const [form, setForm] = useState<FormState>({
    name: '',
    description: '',
    type: 'support',
    status: 'active',
    tone: 'friendly',
    language: 'English',
    greeting_message: 'Hello! How can I help you today?',
    goodbye_message: 'Thank you for chatting with us!',
    custom_instructions: '',
    ai_provider: 'gemini',
    ai_model: 'gemini-2.5-flash',
    ai_temperature: 0.7,
    ai_max_tokens: 1024,
  });

  // Helper to update form state
  const updateForm = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  // ---- Template selection handler ----

  const handleSelectTemplate = (template: TemplateDefinition) => {
    setSelectedTemplate(template.type);

    setForm({
      name: template.defaults.name,
      description: template.defaults.description,
      type: template.type,
      status: 'active',
      tone: template.defaults.tone,
      language: template.defaults.language,
      greeting_message: template.defaults.greeting_message,
      goodbye_message: template.defaults.goodbye_message,
      custom_instructions: template.defaults.custom_instructions,
      ai_provider: 'gemini',
      ai_model: 'gemini-2.5-flash',
      ai_temperature: 0.7,
      ai_max_tokens: 1024,
    });

    setStep('configure');
  };

  const handleBackToTemplates = () => {
    setStep('template');
    setSelectedTemplate(null);
  };

  // ---- Submit ----

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('Agent name is required');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        type: form.type,
        status: form.status,
        configuration: {
          personality: {
            tone: form.tone,
            language: form.language,
            greeting_message: form.greeting_message,
            goodbye_message: form.goodbye_message || 'Thank you for chatting with us!',
            custom_instructions: form.custom_instructions,
          },
          knowledge_base_ids: [],
          data_collection_fields: [],
          escalation_rules: [],
          checkpoints: [],
          document_templates: [],
          trigger_keywords: [],
          goals: [],
          ai_model: {
            provider: form.ai_provider,
            model: form.ai_model,
            temperature: form.ai_temperature,
            max_tokens: form.ai_max_tokens,
          },
        },
      };

      const agent = await createAgent(payload);

      if (pendingFiles.length > 0) {
        try {
          await knowledgeService.uploadFilesToAgent(agent.id, pendingFiles);
          toast.success(`Agent created with ${pendingFiles.length} document(s) uploaded!`);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          toast.success('Agent created! Some files failed to upload.');
        }
      } else {
        toast.success('Agent created successfully!');
      }

      navigate(`/agents/${agent.id}`);
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create agent');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Current template label for header ----

  const currentTemplateLabel =
    TEMPLATES.find((t) => t.type === selectedTemplate)?.label ?? 'New';

  // =========================================================================
  // Step 1 - Template Selection
  // =========================================================================

  if (step === 'template') {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-4">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900">Create a New Agent</h1>
          <p className="text-gray-500 mt-2 text-base">Choose a template to get started quickly</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl w-full">
          {TEMPLATES.map((tpl) => {
            const Icon = tpl.icon;
            return (
              <button
                key={tpl.type}
                type="button"
                onClick={() => handleSelectTemplate(tpl)}
                className={cn(
                  'bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-6 cursor-pointer transition-all text-left',
                  'hover:shadow-[4px_4px_0px_#1a1a2e]',
                  'focus:outline-none focus:ring-2 focus:ring-[#1a1a2e]/20',
                  'w-full min-h-[180px] flex flex-col',
                )}
              >
                <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center mb-3', tpl.iconBg)}>
                  <Icon className={cn('w-5 h-5', {
                    'text-rose-600': tpl.type === 'sales',
                    'text-blue-600': tpl.type === 'support',
                    'text-amber-600': tpl.type === 'onboarding',
                    'text-violet-600': tpl.type === 'lead_generation',
                    'text-gray-600': tpl.type === 'custom',
                  })} />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{tpl.label}</h3>
                <p className="text-sm text-gray-500 leading-snug">{tpl.description}</p>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => navigate('/agents')}
          className="mt-8 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    );
  }

  // =========================================================================
  // Step 2 - Configuration Form
  // =========================================================================

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)]">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[#1a1a2e] px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button
            type="button"
            onClick={handleBackToTemplates}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 text-gray-500" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                Create {currentTemplateLabel} Agent
              </h1>
              <span className="text-xs text-gray-400 capitalize">
                {selectedTemplate?.replace('_', ' ')}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className={cn(
              'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all',
              'bg-violet-500 text-white hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Bot className="w-4 h-4" />
            )}
            Create Agent
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-3xl mx-auto space-y-5">
          {/* ======== Section 1: Profile ======== */}
          <SectionCard
            title="Profile"
            icon={<User className="w-4 h-4 text-violet-600" />}
            iconBg="bg-violet-50"
          >
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs text-gray-400 block mb-2">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateForm({ name: e.target.value })}
                  placeholder="e.g., Customer Support Agent"
                  className={INPUT_CLASS}
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-gray-400 block mb-2">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => updateForm({ description: e.target.value })}
                  placeholder="Describe what this agent will do..."
                  rows={4}
                  className={TEXTAREA_CLASS}
                />
              </div>

              {/* Status pills */}
              <div>
                <label className="text-xs text-gray-400 block mb-2">Status</label>
                <div className="flex gap-2">
                  {(['draft', 'active', 'testing'] as const).map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => updateForm({ status })}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all capitalize',
                        form.status === status
                          ? 'border-violet-500 bg-violet-50 text-violet-700'
                          : 'border-gray-150 text-gray-500 hover:border-gray-300',
                      )}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ======== Section 2: Personality ======== */}
          <SectionCard
            title="Personality"
            icon={<Sparkles className="w-4 h-4 text-violet-600" />}
            iconBg="bg-violet-50"
          >
            <div className="space-y-4">
              {/* Tone pills */}
              <div>
                <label className="text-xs text-gray-400 block mb-2">Tone</label>
                <div className="flex gap-2">
                  {(['professional', 'friendly', 'casual'] as const).map((tone) => (
                    <button
                      key={tone}
                      type="button"
                      onClick={() => updateForm({ tone })}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all capitalize',
                        form.tone === tone
                          ? 'border-violet-500 bg-violet-50 text-violet-700'
                          : 'border-gray-150 text-gray-500 hover:border-gray-300',
                      )}
                    >
                      {tone}
                    </button>
                  ))}
                </div>
              </div>

              {/* Greeting Message */}
              <MarkdownEditor
                label="Greeting Message"
                tooltip="The first message your agent sends when a new conversation starts."
                value={form.greeting_message}
                onChange={(v) => updateForm({ greeting_message: v })}
                placeholder="What should the agent say first?"
                rows={5}
              />

              {/* Custom Instructions */}
              <MarkdownEditor
                label="Custom Instructions"
                tooltip="The core behavioral prompt for your agent. Write detailed instructions about how the agent should respond, what topics to cover, and what to avoid."
                value={form.custom_instructions}
                onChange={(v) => updateForm({ custom_instructions: v })}
                placeholder="Provide specific instructions for how the agent should behave..."
                rows={8}
              />

              {/* Language */}
              <div>
                <label className="text-xs text-gray-400 block mb-2">Language</label>
                <input
                  type="text"
                  value={form.language}
                  onChange={(e) => updateForm({ language: e.target.value })}
                  placeholder="English"
                  className={INPUT_CLASS}
                />
              </div>
            </div>
          </SectionCard>

          {/* ======== Section 4: Knowledge (optional) ======== */}
          <SectionCard
            title="Knowledge"
            subtitle="Optional - upload documents the agent can reference"
            icon={<Database className="w-4 h-4 text-emerald-600" />}
            iconBg="bg-emerald-50"
          >
            <FileUploadZone pendingFiles={pendingFiles} onFilesChange={setPendingFiles} />
          </SectionCard>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="sticky bottom-0 z-10 bg-white border-t-2 border-[#1a1a2e] px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            type="button"
            onClick={handleBackToTemplates}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className={cn(
              'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all',
              'bg-violet-500 text-white hover:bg-violet-600 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Bot className="w-4 h-4" />
            )}
            Create Agent
          </button>
        </div>
      </div>
    </div>
  );
};
